import { usePrescreeningData } from '@/context/pre-screening-context';
import { formatPhoneNumberToInternational } from '@/utils/phone-number';
import { Typography } from '@montugroup/design-system';
import { Box, styled } from '@mui/material';

const ResendSmsInfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
}));

const ResendInfoHeading = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(4.5),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(4),
}));

const StyledTypography = styled(Typography)(() => ({
  lineHeight: 1.5,
}));

const InitialRetryInfo = ({ phoneNumber }: { phoneNumber: string }) => (
  <>
    <ResendInfoHeading>We’ve sent the SMS to {phoneNumber}</ResendInfoHeading>
    <StyledTypography>
      There may be a short delay with your mobile provider. You can try resending the SMS shortly.
    </StyledTypography>
    <StyledTypography fontWeight={'bold'}>Please note:</StyledTypography>
    <StyledTypography>
      If you’ve previously signed up to Alternaleaf and your number has changed, unfortunately you won’t be able to
      speak to a nurse straight away.
    </StyledTypography>
    <StyledTypography>
      Instead, you can book in a consultation with a nurse at a later time and update your personal details.
    </StyledTypography>
  </>
);

const TechnicalIssueInfo = ({ phoneNumber, attemptsRemaining }: { phoneNumber: string; attemptsRemaining: number }) => (
  <>
    <ResendInfoHeading>We’ve sent the SMS to {phoneNumber}</ResendInfoHeading>
    <StyledTypography>
      If the code still hasn’t arrived after waiting a minute, it may be due to a technical issue.
    </StyledTypography>
    <StyledTypography>
      You may want to try again at a later time or you can book in a scheduled consultation instead.
    </StyledTypography>
    <StyledTypography>
      You have {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining.
    </StyledTypography>
    <StyledTypography fontWeight={'bold'}>Please note:</StyledTypography>
    <StyledTypography>
      If you’ve previously signed up to Alternaleaf and your number has changed, unfortunately you won’t be able to
      speak to a nurse straight away.
    </StyledTypography>
    <StyledTypography>
      Instead, you can book in a consultation with a nurse at a later time and update your personal details.
    </StyledTypography>
  </>
);

const MaximumAttemptsReachedInfo = ({ nextAttempt }: { nextAttempt: string }) => (
  <>
    <ResendInfoHeading>Maximum attempts reached</ResendInfoHeading>
    <StyledTypography>You’ve reached the maximum number of resend attempts.</StyledTypography>
    <StyledTypography>To keep your account secure, we’ve paused further attempts for now.</StyledTypography>
    <StyledTypography>You can try again in {nextAttempt} or book a consultation for later.</StyledTypography>
  </>
);

const renderSmsRetryInfo = ({ phoneNumber, screenIndex }: { phoneNumber: string; screenIndex: number }) => {
  switch (screenIndex) {
    case 0:
      return <InitialRetryInfo phoneNumber={phoneNumber} />;
    case 1:
      return <InitialRetryInfo phoneNumber={phoneNumber} />;
    case 2:
      return <TechnicalIssueInfo phoneNumber={phoneNumber} attemptsRemaining={2} />;
    case 3:
      return <TechnicalIssueInfo phoneNumber={phoneNumber} attemptsRemaining={1} />;
    case 4:
      return <MaximumAttemptsReachedInfo nextAttempt={'1 hour'} />;
    case 5:
      return <MaximumAttemptsReachedInfo nextAttempt={'24 hours'} />;
    default:
      return <InitialRetryInfo phoneNumber={phoneNumber} />;
  }
};

const ResendSmsInfo = () => {
  const { preScreeningData } = usePrescreeningData();

  return (
    <ResendSmsInfoSection data-testid={'resend-sms-info'}>
      {renderSmsRetryInfo({
        phoneNumber: formatPhoneNumberToInternational(preScreeningData?.patientDetails.phone ?? ''),
        screenIndex: preScreeningData?.smsVerification.rateLimitIndex || 0,
      })}
    </ResendSmsInfoSection>
  );
};

export default ResendSmsInfo;
